import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import "./Therapy.css";
import "./TherapyPages.css";

function TherapyPolicies() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="page-header">
                <div className="demo-container">
                    <p className="demo-eyebrow">Policies</p>
                    <h1>The fine print, in plain language.</h1>
                    <p className="page-lede">
                        These are the policies that govern how we work with our
                        clients. You'll receive the full versions in your intake
                        packet — this page is the readable summary.
                    </p>
                </div>
            </section>

            <section className="demo-section">
                <div className="demo-container policies-container">
                    <nav className="policies-toc" aria-label="Policies">
                        <h4>On this page</h4>
                        <a href="#cancellation">Cancellation &amp; rescheduling</a>
                        <a href="#payment">Payment</a>
                        <a href="#confidentiality">Confidentiality</a>
                        <a href="#communication">Communication between sessions</a>
                        <a href="#emergencies">Emergencies &amp; crisis</a>
                        <a href="#records">Records &amp; HIPAA</a>
                        <a href="#termination">Ending therapy</a>
                    </nav>

                    <div className="policies-content">
                        <article id="cancellation" className="policy-section">
                            <h2>Cancellation &amp; rescheduling</h2>
                            <p>
                                Sessions canceled or rescheduled with less than 24
                                hours notice are charged the full session fee. This
                                applies to both in-person and telehealth sessions.
                            </p>
                            <p>
                                The reason: when you book a slot, that time is held
                                exclusively for you. With less than 24 hours, your
                                therapist can't reliably fill it.
                            </p>
                            <p>
                                Insurance does not reimburse for late-cancellation
                                fees. The fee is your responsibility.
                            </p>
                            <p>
                                If you're sick, snowed in, or have a genuine
                                emergency, telehealth is almost always an option.
                                Reach out to your therapist and we'll figure it out.
                            </p>
                        </article>

                        <article id="payment" className="policy-section">
                            <h2>Payment</h2>
                            <p>
                                Payment is due at the time of session. A credit card,
                                HSA/FSA card, or bank account on file is required to
                                schedule.
                            </p>
                            <p>
                                Harbor Therapy is an out-of-network practice. We
                                provide a monthly superbill you can submit to your
                                insurance for reimbursement based on your
                                out-of-network mental health benefits. Fees vary by
                                clinician and are listed on each therapist's profile.
                            </p>
                            <p>
                                Per the No Surprises Act, you have the right to
                                receive a Good Faith Estimate of expected charges
                                from any healthcare provider. You'll receive yours at
                                intake.
                            </p>
                        </article>

                        <article id="confidentiality" className="policy-section">
                            <h2>Confidentiality</h2>
                            <p>
                                What you share in session is confidential. Your
                                therapist will not share it with anyone — including
                                family members, employers, or insurance companies —
                                without your written consent.
                            </p>
                            <p>
                                Within Harbor Therapy, your therapist may discuss
                                clinical work in our weekly case consultation
                                meetings, with appropriate de-identification. Your
                                identity and identifying details are not shared, and
                                only your specific therapist has direct access to
                                your records.
                            </p>
                            <p>
                                There are legal exceptions to confidentiality that
                                apply to all licensed therapists in New York and
                                New Jersey:
                            </p>
                            <ul className="policy-list">
                                <li>
                                    <strong>Imminent risk of harm to self.</strong> If
                                    your therapist believes you are at imminent risk
                                    of suicide, they are legally and ethically
                                    required to take action to keep you safe — which
                                    may include contacting emergency services or a
                                    designated contact.
                                </li>
                                <li>
                                    <strong>Imminent risk of harm to others.</strong>{" "}
                                    If you disclose intent to seriously harm an
                                    identifiable person, your therapist is required
                                    to warn that person and/or notify law enforcement.
                                </li>
                                <li>
                                    <strong>Abuse of a child or vulnerable adult.</strong>{" "}
                                    Suspected abuse or neglect of a child, elderly
                                    person, or person with a disability must be
                                    reported to the appropriate state agency.
                                </li>
                                <li>
                                    <strong>Court order.</strong> A judge can subpoena
                                    therapy records in rare circumstances. Your
                                    therapist will notify you immediately if this
                                    happens and limit disclosure to what is legally
                                    required.
                                </li>
                            </ul>
                            <p>
                                For couples therapy, our clinicians follow a "no
                                secrets" policy: anything one partner shares
                                individually that is clinically relevant to the
                                couples work may be shared with the other partner.
                                Your therapist will discuss this explicitly at
                                intake.
                            </p>
                        </article>

                        <article id="communication" className="policy-section">
                            <h2>Communication between sessions</h2>
                            <p>
                                Brief logistical messages — scheduling, billing
                                questions, sharing a relevant article — are fine
                                through our secure client portal. Your therapist will
                                respond within one business day.
                            </p>
                            <p>
                                Clinical content is for sessions. If something
                                significant comes up between sessions, jot it down to
                                bring in. Our clinicians don't do therapy by message
                                because the medium isn't suited to it and because we
                                want to be fully present when we're working.
                            </p>
                            <p>
                                Our clinicians do not communicate with clients via
                                text message, personal email, or social media. The
                                portal is the only secure channel.
                            </p>
                        </article>

                        <article id="emergencies" className="policy-section policy-warning">
                            <h2>Emergencies &amp; crisis</h2>
                            <p>
                                <strong>
                                    Harbor Therapy does not provide crisis or
                                    emergency services.
                                </strong>{" "}
                                Our clinicians see clients by appointment and do not
                                offer 24/7 coverage.
                            </p>
                            <p>
                                If you are experiencing a mental health emergency:
                            </p>
                            <ul className="policy-list">
                                <li>
                                    <strong>988</strong> — Suicide &amp; Crisis
                                    Lifeline (call or text)
                                </li>
                                <li>
                                    <strong>Text HOME to 741741</strong> — Crisis Text
                                    Line
                                </li>
                                <li>
                                    <strong>911</strong> or your nearest emergency
                                    room for immediate medical or psychiatric
                                    emergencies
                                </li>
                            </ul>
                            <p>
                                After a crisis, reach out to your therapist and we'll
                                either schedule an additional session or coordinate
                                with whoever provided the immediate care.
                            </p>
                        </article>

                        <article id="records" className="policy-section">
                            <h2>Records &amp; HIPAA</h2>
                            <p>
                                Harbor Therapy maintains electronic clinical records
                                using a HIPAA-compliant electronic health record
                                system. All session notes, intake documents, billing
                                records, and portal messages are stored within this
                                secure system. Only your specific therapist has
                                direct access to your file.
                            </p>
                            <p>
                                You have the right to:
                            </p>
                            <ul className="policy-list">
                                <li>Request a copy of your records</li>
                                <li>Request corrections to your records</li>
                                <li>
                                    Receive an accounting of any disclosures of your
                                    records
                                </li>
                                <li>
                                    Receive a full copy of the Notice of Privacy
                                    Practices, which describes how we use and protect
                                    your health information
                                </li>
                            </ul>
                            <p>
                                Records are retained per state regulations: at least
                                six years from the date of last service, or three
                                years past the age of majority for minors.
                            </p>
                        </article>

                        <article id="termination" className="policy-section">
                            <h2>Ending therapy</h2>
                            <p>
                                Therapy ends in a few different ways: you've gotten
                                what you came for, life changes and the work pauses,
                                or it stops feeling useful and a different plan
                                makes sense. All of these are normal.
                            </p>
                            <p>
                                Whenever possible, our clinicians prefer to end with
                                at least one final session to talk about what you
                                did, what worked, what didn't, and what to look out
                                for going forward. You can stop at any time — we
                                just ask that you tell your therapist, so the work
                                can be closed intentionally.
                            </p>
                            <p>
                                If your therapist isn't the right fit, they'll say
                                so. Sometimes that means transferring to another
                                clinician within Harbor Therapy; sometimes it means
                                referring you out. Either way, we'll help you find
                                the right next step.
                            </p>
                        </article>

                        <div className="policy-footer-note">
                            <Icon name="lock" />
                            <p>
                                Last updated: January 2026. The complete policies
                                document is provided to all clients during intake and
                                must be signed before the first session.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Questions about any of this?</h2>
                    <p>
                        Email us directly or bring it up during your free
                        consultation.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("booking")} className="btn-primary">
                            Schedule a consultation
                        </Link>
                        <a
                            href="mailto:hello@harbortherapy.com"
                            className="btn-secondary"
                        >
                            hello@harbortherapy.com
                        </a>
                    </div>
                </div>
            </section>

            <TherapyFooter />
        </main>
    );
}

export default TherapyPolicies;